import type { DirectoryState } from '@/modules/accounts/store'
import type { AssetsState } from '@/modules/projects/assets/store'
import type { DesignsState } from '@/modules/projects/designs/store'
import type { ProjectsState } from '@/modules/projects/store'
import type { NotificationsState } from '@/modules/notifications/store/notifications-slice'
import type { ReviewsState } from '@/modules/projects/review/store'

import { appConfig } from '@/config/app-config'

/**
 * Everything the app holds besides the signed-in user: a copy of what the API sent, plus the in-browser workflow. Held in memory only.
 */
export interface SharedData {
  directory: DirectoryState
  projects: ProjectsState
  assets: AssetsState
  designs: DesignsState
  reviews: ReviewsState
  notifications: NotificationsState
}

export const SHARED_KEYS = ['directory', 'projects', 'assets', 'designs', 'reviews', 'notifications'] as const

/**
 * Where shared data lives and how tabs find each other. The shared data is kept in memory only (see `sync-context`), so this key
 * never reaches the browser's own storage; it exists so the in-memory store, and tests that simulate several tabs, work alike.
 */
export const STORAGE_KEY = 'supplylogic:shared'
export const SESSION_KEY = appConfig.sessionKey
export const CHANNEL_NAME = appConfig.notificationChannel
export const WRITE_LOCK_NAME = `${STORAGE_KEY}:write`

const ENVELOPE_VERSION = 1

/** What is stored under `STORAGE_KEY`. `revision` increases by one on every committed mutation. */
export interface StoredEnvelope {
  version: typeof ENVELOPE_VERSION
  revision: number
  writtenBy: string
  writtenAt: string
  data: SharedData
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isEntityState = (value: unknown) => isRecord(value) && Array.isArray(value.ids) && isRecord(value.entities)
const hasEntityStates = (value: unknown, keys: string[]) => isRecord(value) && keys.every((key) => isEntityState(value[key]))

/** Shape check, not a full schema: enough to refuse corrupt or foreign data and re-seed instead of crashing. */
function isSharedData(value: unknown): value is SharedData {
  if (!isRecord(value)) return false
  return (
    hasEntityStates(value.directory, ['accounts', 'users', 'relationships']) &&
    hasEntityStates(value.projects, ['projects', 'projectUsers', 'events']) &&
    hasEntityStates(value.assets, ['assets', 'extractions']) &&
    hasEntityStates(value.designs, ['masters', 'versions', 'variations', 'workingCopies']) &&
    hasEntityStates(value.reviews, ['assignments', 'comments', 'decisions', 'deliverables']) &&
    isEntityState(value.notifications)
  )
}

export function readEnvelope(storage: Storage): StoredEnvelope | null {
  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed) || parsed.version !== ENVELOPE_VERSION) return null
    if (typeof parsed.revision !== 'number' || !isSharedData(parsed.data)) return null
    return parsed as unknown as StoredEnvelope
  } catch {
    return null
  }
}

/** Throws if the browser refuses the write (e.g. quota exceeded). Callers handle that. */
export function writeEnvelope(storage: Storage, envelope: StoredEnvelope) {
  storage.setItem(STORAGE_KEY, JSON.stringify(envelope))
}

export function createEnvelope(data: SharedData, revision: number, writtenBy: string): StoredEnvelope {
  return { version: ENVELOPE_VERSION, revision, writtenBy, writtenAt: new Date().toISOString(), data }
}
